import React, { ChangeEvent, useCallback, useContext, useEffect, useState } from 'react';
import { Box, FormControl, FormControlLabel, FormGroup, Grid, Input, InputLabel, MenuItem, Select, Switch, Theme, Typography, useMediaQuery, useTheme } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import TextField from '@material-ui/core/TextField';
import { FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import { getOrgUnitCategories, getOrgUnitsByCategory, OrgUnitCategory, OrgUnit } from '../../api/orgUnit/orgUnitApi';
import Loading from '../../components/common/Loading/Loading';
import { AlertContext } from '../../contexts/AlertContext';
import { CustomButton, CustomSelect, EmptyTopBar, Label } from '../../components';
import { useTranslation } from 'react-i18next';
import i18nNamespaces from '../../const/i18nNamespaces';
import useStyles from './NewReferralPageStyles';
import { IssueReferralResponse, Observation, Procedure } from '../../types/referrals';
import { SIDES } from '../../const/referral';
import ObservationInputs from '../../components/referral/ObservationInputs/ObservationInputs';
import ProceduresInputs from '../../components/referral/ProceduresInputs/ProceduresInputs';
import Icd10CodeAutocomplete from '../../components/referral/Icd10CodeAutocomplete/Icd10CodeAutocomplete';
import { referralApi } from '../../api/referral/referralApi';
import { useHistory, useParams } from 'react-router-dom';
import appointmentsProvider from '../../services/appointmentsProvider';
import { AppointmentDetailsForPrescriptionDto } from '../../types/appointments';
import { REFERRAL_ISSUED_PAGE } from '../../const/routes';
import { P1Response } from '../../types/p1Service';
import ReferralP1MessagePopup from '../../components/referral/ReferralP1MessagePopup/ReferralP1MessagePopup';
import sentryService from '../../services/sentryService';
import { mapAppointmentDetailsToReferral } from '../../utils/referral';


export type ReferralFormValues = {
	referralType: OrgUnitCategory | null;
	referralTarget: OrgUnit | null;
	isUrgent: boolean;
	principalObservation: Observation | null;
	secondaryObservations: Observation[];
	procedures: Procedure[];
	comment: string;
}

const mapObservationToRequest = (observation: Observation) => ({
	icd10Code: observation.icd10Code.code,
	targetSite: observation.targetSite
});


const NewReferralPage = () => {
	const isDesktop = useMediaQuery<Theme>(theme => theme.breakpoints.up('sm'));
	const classes = useStyles();
   const openAlert = useContext(AlertContext);
   const params = useParams<{ appointmentId: string }>();
   // Referral Type
    const [categories, setCategories] = useState<OrgUnitCategory[]>([]);
   const [categoriesLoading, setCategoriesLoading] = useState(false);

   // Referral Target
	const [targets, setTargets] = useState<OrgUnit[]>([]);
   const [targetsLoading, setTargetsLoading] = useState(false);
   const [targetInput, setTargetInput] = useState('');

   const [loading, setLoading] = useState(false);
   const [appointmentDetails, setAppointmentDetails] =
		   useState<AppointmentDetailsForPrescriptionDto | null>(null);

   const theme = useTheme();
   const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

   const {t} = useTranslation(i18nNamespaces.REFERRAL);
   const {t: errorT} = useTranslation(i18nNamespaces.ERRORS);
   const history = useHistory();

   const [issueReferralResponse, setIssueReferralResponse] = useState<P1Response<IssueReferralResponse> | null>(null);

const patientName = React.useMemo(() => {
	if(appointmentDetails?.child)
		return `${appointmentDetails.child.firstName} ${appointmentDetails.child.surname}`;
	if (appointmentDetails?.patient)
		return `${appointmentDetails.patient.firstName} ${appointmentDetails.patient.surname}`;
	return '';
}, [appointmentDetails?.patient, appointmentDetails?.child]);

   const validationSchema = Yup.object({
		referralType: Yup.object().nullable().required(t('selectReferralType')),
		referralTarget: Yup.object().nullable().required(t('selectReferralTarget')),
		principalObservation: Yup.object()
			.test(
				'icd10-required',
				t('icd10Required'),
				value => !!value && !!value.icd10Code
			).nullable().required(t('principalObservationRequired')),
		secondaryObservations: Yup.array()
			.of(
				Yup.object().test(
					'icd10-required',
					t('icd10Required'),
					value => !!value && !!value.icd10Code
				).nullable(),
			),
		procedures: Yup.array()
			.test(
				'procedure-or-comment',
				t('atLeastOneProcedureOrCommentIsRequired'),
				function (procedures) {
					const { comment } = this.parent;
					const hasProcedure = Array.isArray(procedures) && procedures.some((p: any) => !!p.icd9Code);
					return hasProcedure || (comment && comment.trim().length > 0);
				}
			),
		comment: Yup.string(),
	});

	useEffect(() => {
		if(!params.appointmentId) return;
		(async () => {
			try {
				setLoading(true);
				const { data } =
					await appointmentsProvider.getAppointmentDetailsForPrescription(
						params.appointmentId,
					);
				setAppointmentDetails(data);
			} catch (error) {
				console.error(error);
			} finally {
				setLoading(false);
			}
		})();
	}, [params.appointmentId]);


   // Debounced fetch for categories
   const fetchCategories = useCallback(async () => {
		   setCategoriesLoading(true);
		   try {
			   const {data} = await getOrgUnitCategories();
               if(data.isSuccess)
                setCategories(data.result);
		   } catch {
			   openAlert({ severity: 'error', message: t('errorLoadingReferralTypes') });
		   } finally {
			   setCategoriesLoading(false);
		   }
	   },
	   [openAlert, t]
   );

   // Debounced fetch for targets
   const fetchTargets = useCallback(async (categoryCode: string) => {
		   setTargetsLoading(true);
		   try {
			   const {data} = await getOrgUnitsByCategory(categoryCode);
               if(data.isSuccess)
				   setTargets(data.result);
		   } catch {
			   openAlert({ severity: 'error', message: t('errorLoadingReferralTargets') });
		   } finally {
			   setTargetsLoading(false);
		   }
	   },
	   [openAlert]
   );

   const handleIssueReferralError = (errorResult: P1Response<IssueReferralResponse>) => {
		setIssueReferralResponse(errorResult);
	};

	const formik = useFormik<ReferralFormValues>({
		initialValues: {
			referralType: null,
			referralTarget: null,
			isUrgent: false,
			principalObservation: null as Observation | null,
			secondaryObservations: [] as Observation[],
			procedures: [{icd9Code: null}] as Procedure[],
			comment: '',
		},
		validationSchema,
		onSubmit: async (values) => {
		try{
			setLoading(true);
			const {data} = await referralApi.issueReferral({
				...mapAppointmentDetailsToReferral(appointmentDetails),
				orgUnitSpecialtyCode: values.referralTarget.code,
				isUrgent: values.isUrgent,
				principalObservation: mapObservationToRequest(values.principalObservation!),
				secondaryObservations: values.secondaryObservations.map(mapObservationToRequest),
				procedures: values.procedures.map(p => p.icd9Code ? ({icd9Code: p.icd9Code?.code}) : null).filter(Boolean),
				comment: values.comment
			});


			if (data.isSuccess && data.result.results.length === 0) {
				history.push(REFERRAL_ISSUED_PAGE, { appointmentId: parseInt(params.appointmentId) });
			}
			else {
				handleIssueReferralError(data);		
				sentryService.captureReferralException(params.appointmentId, data);
			}

		} catch (error) {
			openAlert({
				severity: 'error',
				message: errorT('unexpectedErrorOccurred'),
			});
			console.error(error);
		} finally {
			setLoading(false);
		}
		},
	});



	   // Fetch categories on mount
	   useEffect(() => {
		   fetchCategories();
	   }, [fetchCategories]);

	   // Fetch targets when referralType or input changes
	   useEffect(() => {
		   if (formik.values.referralType) {
			   fetchTargets(formik.values.referralType.code);
		   } 
           else {
			   setTargets([]);
			   formik.setFieldValue('referralTarget', null);
		   }
	   }, [formik.values.referralType, targetInput, fetchTargets]);

	   const handleCloseP1MessagePopup = () => {
			if(issueReferralResponse?.isSuccess) {
				window.close();
			} else {
				setIssueReferralResponse(null);
			}
		};


	   return (
        <FormikProvider value={formik}>
			<Loading loading={loading} withBackdrop />
            <EmptyTopBar title={isDesktop ? `${t('newReferralFor')} ${patientName}` : null} onCancel={() => window.close()}/>
		   <Box className={classes.container}>
			   <form onSubmit={formik.handleSubmit} autoComplete="off">
				   <Grid container spacing={2}>
					<Grid item xs={12}>
						<Typography variant="h3" style={{ marginBottom: 16 }}>
							{t('issueNewReferral')}
						</Typography>
					</Grid>
					   <Grid item md={6} xs={12}>
						<FormControl
							variant="outlined"
							fullWidth
						>
							<InputLabel id="referralType">
								{t('referralType')} *
							</InputLabel>
							<CustomSelect
								name="referralType"
								label={t('referralType')}
								labelId='referralType'
								value={formik.values.referralType ? formik.values.referralType.code : ''}
								onChange={e => {
									const selected = categories?.find(c => c.code === e.target.value);
									formik.setFieldValue('referralType', selected || null);
								}}
								fullWidth
								variant="outlined"
								error={Boolean(formik.touched.referralType && formik.errors.referralType)}
								disabled={categoriesLoading}
							>
								{categories.length > 0 && categories?.map(option => (
									<MenuItem key={option.code} value={option.code}>
										{option.name}
									</MenuItem>
								))}
							</CustomSelect>
							{formik.touched.referralType && formik.errors.referralType && (
								<span className={classes.errorText}>{formik.errors.referralType}</span>
							)}
						</FormControl>
					   </Grid>
					   <Grid item md={6} xs={12} />
					   <Grid item md={6} xs={12}>
						<Autocomplete
							id="referralTarget"
							options={Array.isArray(targets) ? targets : []}
							loading={targetsLoading}
							getOptionLabel={option => option?.name || ''}
							value={formik.values.referralTarget}
							onChange={(_, value) => {
								formik.setFieldValue('referralTarget', value);
								if (!value) setTargetInput('');
							}}
							inputValue={targetInput}
							onInputChange={(_, value, reason) => {
								if (reason === 'input' || reason === 'reset' || reason === 'clear') 
									setTargetInput(value);
								}}
							getOptionSelected={(option, value) => option?.code === value?.code}
							disabled={!formik.values.referralType}
							renderInput={params => (
								<TextField
									{...params}
									label={t('referralTarget') + ' *'}
									variant="outlined"
									error={Boolean(formik.touched.referralTarget && formik.errors.referralTarget)}
									helperText={formik.touched.referralTarget && formik.errors.referralTarget}
									InputProps={{
										...params.InputProps,
										endAdornment: (
											<>
												{targetsLoading ? <Loading loading size={20} /> : null}
												{params.InputProps.endAdornment}
											</>
										),
									}}
								/>
							)}
						/>
					   </Grid>
					   <Grid item md={6} xs={12} />
					   <Grid item md={6} xs={12}>
							<FormControlLabel
									control={
										<Switch
											checked={formik.values.isUrgent}
											onChange={(
												e: ChangeEvent<HTMLInputElement>,
											) =>
												formik.handleChange(e)
											}
											color={'secondary'}
											name={'isUrgent'}
										/>
									}
									label={
										<Box style={{fontSize: 16}}>
											{t('urgent')}
										</Box>
									}
									className={classes.switchField}
								/>
				       </Grid>
					   <div className={classes.mobileSpacer} />
					   <Grid item xs={12}>
							<Typography variant="h3" className={classes.sectionTitle}>
								{t('observation')}
							</Typography>
						</Grid>
					   <Grid item md={6} xs={12}>
							<Icd10CodeAutocomplete
								label={t('principalObservation') + ' *'}
								icd10Code={formik.values.principalObservation?.icd10Code || null}
								onChange={newValue =>
									formik.setFieldValue('principalObservation.icd10Code', newValue)
								}
								error={
									formik.touched.principalObservation && formik.touched.principalObservation &&
									Boolean(formik.errors.principalObservation)
								}
								helperText={
									formik.touched.principalObservation && formik.touched.principalObservation &&
									formik.errors.principalObservation
								}
							/>
						</Grid>
						<Grid item md={6} xs={12}>
							<FormControl
								variant="outlined"
								fullWidth
							>
								<InputLabel id="principalObservation.targetSite">
									{t('targetSite')}
								</InputLabel>
								<CustomSelect
									name="principalObservation.targetSite"
									label={t('targetSite')}
									labelId='principalObservation.targetSite'
									value={formik.values.principalObservation?.targetSite ? formik.values.principalObservation?.targetSite : ''}
									onChange={formik.handleChange}
									fullWidth
									variant="outlined"
									error={Boolean(formik.touched.principalObservation?.targetSite && formik.errors.principalObservation?.targetSite)}
									disabled={categoriesLoading}
								>
									{Object.values(SIDES).map(side => (
										<MenuItem key={side} value={side}>
											{t('side.' + side)}
										</MenuItem>
									))}
								</CustomSelect>
								{formik.touched.principalObservation?.targetSite && formik.errors.principalObservation?.targetSite && (
									<span className={classes.errorText}>{formik.errors.principalObservation?.targetSite}</span>
								)}
							</FormControl>
						</Grid>
						<ObservationInputs />
						<div className={classes.mobileSpacer} />
						<Grid item xs={12}>
							<Typography variant="h3" className={classes.sectionTitle}>
								{t('procedures')}
							</Typography>
						</Grid>        
						<ProceduresInputs />
						<Grid item md={6} xs={12}>
							<TextField
								label={t('comment')}
								name="comment"
								value={formik.values.comment}
								onChange={formik.handleChange}
								multiline
								rows={4}
								variant="outlined"
								fullWidth
								InputProps={{
									style: {
										height: 'auto',
									},                      
								}}
								error={Boolean(formik.touched.procedures && formik.errors.procedures)}
								helperText={formik.touched.procedures && formik.errors.procedures}
							/>
						</Grid>
						<Grid item md={6} xs={12} />
						<Grid item md={6} xs={12}>
						</Grid>
						<Grid item md={6} xs={12} style={{ display: 'flex', justifyContent: 'flex-end' }}>
							 <CustomButton
								type="submit"
								variant="contained"
								color="secondary"
								style={{padding: '0 80px'}}
								fullWidth={isMobile}
							>
								{t('issueReferral')}
							</CustomButton>
						</Grid>
				   </Grid>
			   </form>
		   </Box>
		   <ReferralP1MessagePopup
                open={!!issueReferralResponse?.result?.results?.length} 
                onClose={handleCloseP1MessagePopup} 
                p1Response={issueReferralResponse} 
            />
           </FormikProvider>
	   );
};

export default NewReferralPage;
